/**
 * Pool / Spa (POOL DESIGNER round, 2026-10-02 — kernel pool.ts holds the design rules).
 * A closed `boundary` (curves allowed, Edit Boundary) like a floor. The TYPE carries the
 * firm's build-up standards — coping, bond beam, waterline tile, cove, shell — and the
 * default finishes; the instance carries depths, the depth direction, finish overrides
 * and the features. Per-edge choices (raised beam, spillover, vanishing edge) live on
 * the boundary edges (PathNode.edge).
 */
import type { CategorySchema } from '../../registry'

export const poolSchema: CategorySchema = {
  id: 'pool',
  label: 'Pool',
  pickRank: 3,
  pathParamKey: 'boundary',
  typeParams: {
    groups: [
      {
        label: 'Pool',
        params: [
          {
            key: 'poolKind', label: 'Kind', kind: 'choice', default: 'pool',
            choices: [{ value: 'pool', label: 'Pool' }, { value: 'spa', label: 'Spa' }],
          },
          { key: 'defaultShallowFt', label: 'Shallow Depth', kind: 'length', default: 3.5, min: 0.5, max: 20 },
          { key: 'defaultDeepFt', label: 'Deep Depth', kind: 'length', default: 6, min: 0.5, max: 20 },
          { key: 'coveRadiusFt', label: 'Wall-to-Floor Cove Radius', kind: 'length', default: 1, min: 0, max: 3 },
          { key: 'waterBelowCopingFt', label: 'Water Below Coping', kind: 'length', default: 0.5, min: 0, max: 2 },
          { key: 'shellThicknessFt', label: 'Shell Thickness', kind: 'length', default: 8 / 12, min: 0.25, max: 2 },
        ],
      },
      {
        label: 'Coping',
        params: [
          { key: 'copingWidthFt', label: 'Coping Width', kind: 'length', default: 1, min: 0.25, max: 4 },
          { key: 'copingOverhangFt', label: 'Coping Overhang', kind: 'length', default: 1 / 12, min: 0, max: 0.5 },
          { key: 'copingThicknessFt', label: 'Coping Thickness', kind: 'length', default: 2 / 12, min: 0.05, max: 1 },
        ],
      },
      {
        label: 'Bond Beam',
        params: [
          { key: 'bondBeamWidthFt', label: 'Bond Beam Width', kind: 'length', default: 1, min: 0.25, max: 3 },
          { key: 'bondBeamDepthFt', label: 'Bond Beam Depth', kind: 'length', default: 1, min: 0.25, max: 3 },
        ],
      },
      {
        label: 'Finishes',
        params: [
          { key: 'waterlineTileFt', label: 'Waterline Tile Height', kind: 'length', default: 0.5, min: 0, max: 2 },
          { key: 'basinMaterial', label: 'Basin Finish', kind: 'material', default: '' },
          { key: 'waterlineMaterial', label: 'Waterline Tile', kind: 'material', default: '' },
          { key: 'copingMaterial', label: 'Coping', kind: 'material', default: '' },
        ],
      },
    ],
  },
  instanceParams: {
    groups: [
      {
        label: 'Constraints',
        params: [
          { key: 'boundary', label: 'Boundary', kind: 'path2d', default: null },
          { key: 'heightOffsetFt', label: 'Coping Top From Level', kind: 'length', default: 0, min: -20, max: 20 },
        ],
      },
      {
        label: 'Depth',
        params: [
          // blank = the TYPE's default depth (the casework heightFt nullable-override pattern)
          { key: 'shallowDepthFt', label: 'Shallow Depth', kind: 'length', default: null, optional: true, min: 0.5, max: 20 },
          { key: 'deepDepthFt', label: 'Deep Depth', kind: 'length', default: null, optional: true, min: 0.5, max: 20 },
          { key: 'transitionStart', label: 'Slope Starts (0-1)', kind: 'number', default: 0.4, min: 0, max: 1 },
          { key: 'transitionEnd', label: 'Slope Ends (0-1)', kind: 'number', default: 0.6, min: 0, max: 1 },
          // { a, b } shallow end → deep end; set with the "Set depth direction" action
          { key: 'depthAxis', label: 'Depth Direction', kind: 'data', default: null, hidden: true, positional: true },
        ],
      },
      {
        label: 'Finish Overrides',
        params: [
          { key: 'basinMaterialOverride', label: 'Basin Finish', kind: 'material', default: '' },
          { key: 'waterlineMaterialOverride', label: 'Waterline Tile', kind: 'material', default: '' },
          { key: 'copingMaterialOverride', label: 'Coping', kind: 'material', default: '' },
        ],
      },
      {
        label: 'Features',
        params: [
          { key: 'poolFeatures', label: 'Features', kind: 'data', default: null, hidden: true },
        ],
      },
    ],
  },
}
